"use client";

import { QuizQuestion as QuestionType } from "@/lib/daily/types";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { useMemo } from "react";

interface QuizQuestionProps {
  readonly question: QuestionType;
  readonly selectedOptionIndex: number | null;
  readonly isSubmitted: boolean;
  readonly onSelectOption: (index: number) => void;
}

export default function QuizQuestion({
  question,
  selectedOptionIndex,
  isSubmitted,
  onSelectOption,
}: QuizQuestionProps) {
  const letters = ["A", "B", "C", "D"];

  const shuffledOptions = useMemo(() => {
    const optionsWithIndex = question.options.map((option, index) => ({
      option,
      originalIndex: index,
      isCorrect: index === question.correctIndex,
    }));

    let seed = `${question.id}:${question.question}`.split("").reduce((hash, char) => {
      return (hash * 31 + char.charCodeAt(0)) >>> 0;
    }, 2166136261);

    const shuffled = [...optionsWithIndex];
    for (let i = shuffled.length - 1; i > 0; i--) {
      seed = (seed * 1664525 + 1013904223) >>> 0;
      const j = seed % (i + 1);
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    return shuffled;
  }, [question.correctIndex, question.id, question.options, question.question]);

  return (
    <div className="anim-slide-up" style={{ width: "100%" }}>
      <h3 className="quiz-question-text">{question.question}</h3>

      <div className="quiz-options-list">
        {shuffledOptions.map((item, displayIndex) => {
          const isSelected = selectedOptionIndex === item.originalIndex;
          const isCorrectAnswer = item.isCorrect;

          let cardClass = "quiz-option-card";
          if (isSelected) cardClass += " selected";

          if (isSubmitted) {
            if (isCorrectAnswer) {
              cardClass += " correct";
            } else if (isSelected) {
              cardClass += " incorrect";
            }
          }

          const handleOptionClick = () => {
            if (isSubmitted) return;
            onSelectOption(item.originalIndex);
          };

          return (
            <button
              key={item.originalIndex}
              className={cardClass}
              onClick={handleOptionClick}
              disabled={isSubmitted}
              style={{ width: "100%" }}
            >
              <div className="quiz-option-letter">{letters[displayIndex]}</div>
              <span style={{ flex: 1 }}>{item.option}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}